import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { WorkoutLayer } from '@strength/db-schema';
import {
  mergeWorkoutLayers,
  Workout,
  WorkoutSection,
} from '@strength/domain-workout';
import { MdDragIndicator, MdOutlineChevronRight } from '@teamworksdev/icons';
import { Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { cn } from '../../../../components/utils';
import { wrapTemplateLayerAsHasWorkoutLayer } from '../workout-template-utils';

/**
 * Summary line for a day's row: "N sections • M exercises".
 * Sections without exercises still count; "No sections" when the day is empty.
 */
const useDaySummary = (layer: WorkoutLayer): string => {
  const { t } = useTranslation();
  return useMemo(() => {
    let workout: Workout | null = null;
    try {
      workout = mergeWorkoutLayers(wrapTemplateLayerAsHasWorkoutLayer(layer));
    } catch {
      return t('noSections');
    }
    const sections: WorkoutSection[] = workout?.sections ?? [];
    if (sections.length === 0) {
      return t('noSections');
    }
    const exerciseCount = sections.reduce(
      (sum, section) =>
        sum +
        section.entries.filter((entry) => entry.__type === 'WorkoutExercise')
          .length,
      0,
    );
    return t('sectionExerciseSummary', {
      // i18next needs `count` present to select the plural variant
      count: sections.length,
      sectionCount: sections.length,
      exerciseCount,
    });
  }, [layer, t]);
};

const DaySummaryText = ({ layer }: { layer: WorkoutLayer }) => {
  const summary = useDaySummary(layer);
  return <span className="text-sm text-gray-500 truncate">{summary}</span>;
};

/**
 * "Selected Workouts" list for multi-day templates.
 * Each row is one day (one template array entry); click a row to drill into
 * that day's sheet (the parent renders it and the Back button). Editable
 * mode also supports drag to reorder. Read-only mode (create drawer): no
 * drag handle. Day count is fixed — rows cannot be added or removed. Emits
 * the full (possibly reordered) layer array; entry index is the day position.
 */
export const WorkoutTemplateDayList = ({
  layers,
  onChange,
  onSelectDay,
  readOnly = false,
}: {
  layers: WorkoutLayer[];
  onChange: (layers: WorkoutLayer[]) => void;
  onSelectDay: (index: number) => void;
  readOnly?: boolean;
}) => {
  const { t } = useTranslation();
  // Stable ids travel with their day through reorders, so React reconciles
  // rows by day, not position.
  const [dayUids, setDayUids] = useState<string[]>(() =>
    layers.map(() => v4()),
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const from = dayUids.indexOf(String(active.id));
    const to = dayUids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onChange(reorderDays(layers, from, to));
    setDayUids((uids) => reorderDays(uids, from, to));
  };

  return (
    <div className="flex flex-col gap-2">
      <Typography
        variant="body2Semibold"
        color="text.secondary"
        className="pt-4"
      >
        {t('selectedWorkouts')}
      </Typography>
      {readOnly ? (
        <div className="flex flex-col gap-2">
          {layers.map((layer, i) => (
            <DayRow
              key={i}
              id={String(i)}
              label={t('workoutNum', { num: i + 1 })}
              layer={layer}
              onClick={() => onSelectDay(i)}
              readOnly
            />
          ))}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={({ active }) => setActiveId(String(active.id))}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <SortableContext
            items={dayUids}
            strategy={verticalListSortingStrategy}
          >
            {dayUids.map((uid, i) => {
              const layer = layers[i];
              if (!layer) return null;
              return (
                <DayRow
                  key={uid}
                  id={uid}
                  label={t('workoutNum', { num: i + 1 })}
                  layer={layer}
                  isDragging={activeId === uid}
                  onClick={() => onSelectDay(i)}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

/** Move the entry at `from` to `to`, shifting the others. Exported for tests. */
export const reorderDays = <T,>(
  entries: T[],
  from: number,
  to: number,
): T[] => {
  const reordered = [...entries];
  const [moved] = reordered.splice(from, 1);
  if (moved === undefined) return entries;
  reordered.splice(to, 0, moved);
  return reordered;
};

/** Replace one day's layer by index. Exported for tests. */
export const updateDayAt = (
  layers: WorkoutLayer[],
  index: number,
  layer: WorkoutLayer,
): WorkoutLayer[] => {
  const updated = [...layers];
  updated[index] = layer;
  return updated;
};

const DayRow = ({
  id,
  label,
  layer,
  isDragging,
  onClick,
  readOnly = false,
}: {
  id: string;
  label: string;
  layer: WorkoutLayer;
  isDragging?: boolean;
  onClick?: () => void;
  readOnly?: boolean;
}) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const dayRowButton = (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('openWorkout', { workoutName: label })}
      className="flex flex-row items-center gap-2 flex-1 min-w-0 text-left cursor-pointer bg-transparent border-none p-0"
    >
      <span className="flex flex-col min-w-0 flex-1">
        <span className="font-medium text-sm truncate">{label}</span>
        <DaySummaryText layer={layer} />
      </span>
      {/* decorative inside the button — the button handles the click */}
      <MdOutlineChevronRight size={18} className="text-gray-400" />
    </button>
  );

  // Read-only rows skip the drag handle but still drill into the day
  if (readOnly) {
    return (
      <div
        className="flex flex-row items-center py-2 px-3 gap-2 bg-gray-50 border border-gray-200 rounded-md"
        aria-label={label}
      >
        {dayRowButton}
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        'flex flex-row items-center py-2 px-3 gap-2 bg-gray-50 border border-gray-200 rounded-md',
        isDragging && 'opacity-50',
      )}
    >
      <div
        {...attributes}
        {...listeners}
        ref={setActivatorNodeRef}
        aria-label={t('dragWorkout')}
        className="cursor-grab flex items-center"
      >
        <MdDragIndicator size={18} className="text-gray-400" />
      </div>
      {dayRowButton}
    </div>
  );
};
